import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Download, Users, BarChart3, Handshake } from 'lucide-react';

const actions = [
  {
    icon: Download,
    title: 'For Tenants',
    subtitle: 'Download the app. Take control.',
    cta: 'Get YAWOTE',
    description: 'Start managing your rental life today',
    gradient: 'from-blue-500 to-cyan-500',
    link: 'https://play.google.com/store/apps/details?id=com.yawote.app',
  },
  {
    icon: BarChart3,
    title: 'For Managers',
    subtitle: 'Automate your portfolio.',
    cta: 'Get Started',
    description: 'See the power of automation',
    gradient: 'from-purple-500 to-pink-500',
    link: 'https://manager.yawote.co.ke',
  },
  {
    icon: Handshake,
    title: 'For Partners',
    subtitle: 'Grow with us.',
    cta: 'Partner Up',
    description: 'Join the ecosystem',
    gradient: 'from-orange-500 to-red-500',
    link: '/partner-form',
  },
];

export default function CTASection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);
  const dotOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.02, 0.04, 0.02]);

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background with parallax */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </motion.div>
      <motion.div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          opacity: dotOpacity,
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', 
          backgroundSize: '40px 40px' 
        }}
      ></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Main Heading */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-4">
            Join the{' '}
            <span className="text-gradient">YAWOTE Revolution.</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Whether you are a tenant tired of cash, a manager tired of delinquency, 
            or an investor looking for the next big thing—your journey starts here.
          </p>
        </motion.div>

        {/* CTA Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {actions.map((action, idx) => {
            const IconComponent = action.icon;
            return (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true }}
              >
                {/* Glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-all duration-500`}></div>
                
                {/* Card */}
                <div className="relative card text-center h-full flex flex-col justify-between hover:scale-[1.02] transition-transform duration-300">
                  <div>
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="text-white" size={32} />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-2">{action.title}</h3>
                    <p className={`font-semibold mb-4 bg-gradient-to-r ${action.gradient} bg-clip-text text-transparent`}>
                      {action.subtitle}
                    </p>
                    <p className="text-gray-400 text-sm mb-8">{action.description}</p>
                  </div>

                  {/* Button */}
                  {action.link && action.link.startsWith('http') ? (
                    <a href={action.link} target="_blank" rel="noopener noreferrer" className="w-full">
                      <button className={`w-full py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${action.gradient} hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group/btn`}>
                        {action.cta}
                        <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </a>
                  ) : (
                    <a href={action.link} className="w-full">
                      <button className={`w-full py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${action.gradient} hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group/btn`}>
                        {action.cta}
                        <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA Banner */}
        <motion.div
          className="relative overflow-hidden rounded-2xl p-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Background with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-cyan-600/20 border border-blue-500/20 rounded-2xl"></div>
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]"></div>
          
          <div className="relative">
            <h3 className="text-3xl font-bold text-white mb-4">Ready to transform rental living?</h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Join millions of renters, managers, and partners who are already experiencing 
              the future of housing.
            </p>
            <a href="https://play.google.com/store/apps/details?id=com.yawote.app" target="_blank" rel="noopener noreferrer">
              <button className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4 group">
                <Download size={22} className="group-hover:scale-110 transition-transform" />
                Download YAWOTE Now
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
